/** Why a notification may not reach a guest, as compact badges.
 *
 *  Blocking reasons ("no channels", "event never fires") outrank advisory ones
 *  ("no sends in 30d") — an operator scanning the page should see the ones that
 *  mean *nothing is being delivered* first.
 */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Chip, Stack, Tooltip } from '@mui/material';

import type { CannotFireReason, WarningCode } from './model/types';
import { WARNING_HELP, WARNING_LABELS, warningSeverity } from './format';

const SEVERITY_RANK = { error: 0, warning: 1, info: 2 } as const;

const ICONS = {
  error: ErrorOutlineIcon,
  warning: WarningAmberIcon,
  info: InfoOutlinedIcon,
} as const;

interface Props {
  warnings: WarningCode[];
  /** Sharpens the "event never fires" tooltip with the actual reason. */
  cannotFireReason?: CannotFireReason | null;
  canonicalEvent?: string | null;
  /** Icon-only, for the compact row. */
  iconOnly?: boolean;
}

function cannotFireHelp(
  reason: CannotFireReason | null | undefined,
  canonical: string | null | undefined,
): string {
  switch (reason) {
    case 'aliased':
      return `This event code is rewritten to "${canonical}" before delivery matches notifications, so a notification saved on this code never matches. Re-save it on "${canonical}".`;
    case 'not_emitted':
      return 'Nothing ever emits an event with this code, so this notification can never be delivered.';
    case 'unresolvable':
      return 'The event factory cannot resolve this code to an event class.';
    default:
      return WARNING_HELP.event_cannot_fire;
  }
}

export function WarningBadges({
  warnings,
  cannotFireReason,
  canonicalEvent,
  iconOnly = false,
}: Props) {
  if (!warnings.length) return null;
  const sorted = [...warnings].sort(
    (a, b) => SEVERITY_RANK[warningSeverity(a)] - SEVERITY_RANK[warningSeverity(b)],
  );

  if (iconOnly) {
    const worst = sorted[0]!;
    const severity = warningSeverity(worst);
    const Icon = ICONS[severity];
    const title = sorted
      .map((w) =>
        w === 'event_cannot_fire'
          ? `${WARNING_LABELS[w]}: ${cannotFireHelp(cannotFireReason, canonicalEvent)}`
          : `${WARNING_LABELS[w]}: ${WARNING_HELP[w]}`,
      )
      .join('\n');
    return (
      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{title}</span>}>
        <Icon
          fontSize="small"
          color={severity === 'info' ? 'disabled' : severity}
          aria-label={sorted.map((w) => WARNING_LABELS[w]).join(', ')}
          sx={{ fontSize: 16 }}
        />
      </Tooltip>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {sorted.map((w) => {
        const severity = warningSeverity(w);
        return (
          <Tooltip
            key={w}
            title={
              w === 'event_cannot_fire'
                ? cannotFireHelp(cannotFireReason, canonicalEvent)
                : WARNING_HELP[w]
            }
          >
            <Chip
              size="small"
              variant="outlined"
              label={WARNING_LABELS[w]}
              color={severity === 'info' ? 'default' : severity}
              sx={{ height: 20, fontSize: '0.6875rem' }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}
